import React from 'react';
import { Link } from 'react-router-dom';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import {
  Stethoscope, Play, Check, Zap, BookOpen, BarChart3, Brain,
  Users, Award, Clock, ArrowRight, ChevronRight
} from 'lucide-react';
import SocialLoginButton from '../components/SocialLoginButton';

const LandingPage = () => {
  const { tenant } = useTenant();
  const tenantName = tenant?.name || 'MedMCQ';

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <Icon3D icon={Stethoscope} colour="turquoise" size="sm" />
              <span className="text-xl font-bold">
                <span className="text-slate-900 dark:text-white">Med</span>
                <span className="text-cyan-500">MCQ</span>
              </span>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium transition-colors">
                Features
              </a>
              <a href="#questions" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium transition-colors">
                Question Bank
              </a>
              <a href="#pricing" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium transition-colors">
                Pricing
              </a>
              <a href="#about" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium transition-colors">
                About
              </a>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              <Link
                to="/login"
                className="px-2 sm:px-4 py-2 text-slate-700 dark:text-slate-300 font-medium hover:text-slate-900 dark:hover:text-white transition-colors whitespace-nowrap text-sm sm:text-base"
              >
                Login
              </Link>
              <Link
                to="/login?register=true"
                className="px-3 sm:px-5 py-2 sm:py-2.5 text-white font-semibold rounded-xl hover:opacity-90 transition-all relative overflow-hidden whitespace-nowrap text-sm sm:text-base"
                style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 dark:bg-cyan-900/30 border border-cyan-200 dark:border-cyan-800 rounded-full mb-6">
                <Icon3D icon={Zap} colour="turquoise" size="xs" />
                <span className="text-sm font-medium text-cyan-700 dark:text-cyan-300">
                  Trusted by Medical Students Worldwide
                </span>
              </div>

              {/* Headline */}
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                <span className="text-slate-900 dark:text-white">Master Medicine</span>
                <br />
                <span className="text-slate-900 dark:text-white">Through </span>
                <span className="text-cyan-500">Smart</span>
                <br />
                <span className="text-cyan-500">Practice</span>
              </h1>

              {/* Description */}
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-8 max-w-lg">
                Prepare for your medical exams with our comprehensive MCQ bank,
                adaptive learning, and detailed explanations created by medical professionals.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <Link
                  to="/login?register=true"
                  className="inline-flex items-center gap-2 px-6 py-3.5 text-white font-semibold rounded-xl hover:opacity-90 transition-all relative overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                  <span className="relative z-10">Start Learning Free</span>
                  <ArrowRight className="w-5 h-5 relative z-10" />
                </Link>
                <button
                  className="inline-flex items-center gap-2 px-6 py-3.5 border-2 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-semibold rounded-xl hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
                  style={{ boxShadow: 'var(--shadow-card)' }}
                >
                  <Icon3D icon={Play} colour="blue" size="xs" />
                  Watch Demo
                </button>
              </div>

              {/* Social sign-in — only shown when credentials are configured */}
              {(process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_APPLE_CLIENT_ID) && (
                <div className="flex items-center gap-3 mb-12">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Or sign in with</span>
                  <SocialLoginButton provider="google" variant="compact" />
                  <SocialLoginButton provider="apple" variant="compact" />
                </div>
              )}
              {!(process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_APPLE_CLIENT_ID) && (
                <div className="mb-12" />
              )}

              {/* Feature List */}
              <ul className="space-y-3">
                {[
                  '95,000+ practice questions across all specialties',
                  'Detailed explanations by medical professionals',
                  'Adaptive learning that focuses on your weak areas',
                  'Track your progress with detailed analytics'
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Icon3D icon={Check} colour="turquoise" size="xs" />
                    <span className="text-slate-700 dark:text-slate-200">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Content - Question Preview Card */}
            <div className="relative">
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
                {/* Question Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                  <span className="px-3 py-1 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-sm font-medium rounded-full border border-cyan-200 dark:border-cyan-800">
                    Cardiology
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-sm">Question 12 of 50</span>
                </div>

                {/* Question Content */}
                <div className="p-6">
                  <p className="text-lg text-slate-800 dark:text-slate-200 leading-relaxed mb-6">
                    A 65-year-old male presents with chest pain radiating to the left arm.
                    ECG shows ST elevation in leads V1-V4. What is the most likely diagnosis?
                  </p>

                  {/* Answer Options */}
                  <div className="space-y-3">
                    {[
                      { letter: 'A', text: 'Unstable Angina', selected: false },
                      { letter: 'B', text: 'Anterior STEMI', selected: true },
                      { letter: 'C', text: 'Pericarditis', selected: false },
                      { letter: 'D', text: 'Aortic Dissection', selected: false },
                    ].map((option) => (
                      <div
                        key={option.letter}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                          option.selected
                            ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                            : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                        }`}
                      >
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center font-semibold ${
                            option.selected
                              ? 'bg-cyan-500 text-white'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                          }`}
                        >
                          {option.letter}
                        </div>
                        <span className={`font-medium ${option.selected ? 'text-cyan-700 dark:text-cyan-300' : 'text-slate-700 dark:text-slate-200'}`}>
                          {option.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-100 dark:bg-cyan-900 rounded-full blur-3xl opacity-60" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-100 dark:bg-blue-900 rounded-full blur-3xl opacity-60" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Everything You Need to Excel
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Comprehensive tools designed specifically for medical students to master their exams.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: BookOpen,
                title: 'Extensive Question Bank',
                description: '95,000+ expertly crafted MCQs covering all medical specialties and exam formats.'
              },
              {
                icon: Brain,
                title: 'Adaptive Learning',
                description: 'AI-powered system that identifies your weak areas and creates personalised study plans.'
              },
              {
                icon: BarChart3,
                title: 'Detailed Analytics',
                description: 'Track your progress with comprehensive performance metrics and insights.'
              },
              {
                icon: Clock,
                title: 'Exam Mode',
                description: 'Simulate real exam conditions with timed tests and realistic question formats.'
              },
              {
                icon: Users,
                title: 'Community Rankings',
                description: 'Compare your progress with peers and stay motivated with leaderboards.'
              },
              {
                icon: Award,
                title: 'Achievements',
                description: 'Earn badges and rewards as you progress through your medical education journey.'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="p-6 bg-slate-50 dark:bg-slate-800 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <div className="mb-4">
                  <Icon3D icon={feature.icon} colour="turquoise" size="md" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-slate-600 dark:text-slate-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '95,000+', label: 'Practice Questions' },
              { value: '18+', label: 'Medical Specialties' },
              { value: '95%', label: 'Pass Rate' },
              { value: '4.9/5', label: 'Student Rating' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-cyan-400 mb-2">{stat.value}</div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Ace Your Medical Exams?
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            Start preparing for your medical exams today with {tenantName}'s comprehensive MCQ platform and adaptive learning.
          </p>
          <Link
            to="/login?register=true"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-cyan-600 font-semibold rounded-xl hover:bg-slate-50 transition-all"
            style={{ boxShadow: 'var(--shadow-3d-btn)' }}
          >
            Get Started Free
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Icon3D icon={Stethoscope} colour="turquoise" size="sm" />
              <span className="text-xl font-bold text-white">
                Med<span className="text-cyan-400">MCQ</span>
              </span>
            </div>

            <p className="text-slate-400 text-sm text-center">
              &copy; 2025 {tenantName}. Medical Student Learning Platform.
              <br className="md:hidden" />
              <span className="hidden md:inline"> | </span>
              For educational purposes only.
            </p>

            <div className="flex items-center gap-6">
              <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors text-sm">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
